import {Download} from '@mui/icons-material';
import {Button, styled} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import Currency from '../../../../components/Currency';
import {ServiceContext} from '../../../../components/Services';
import {BlockColumn, BlockContainer, BlockRow, Blocks, Break, Buttons, Container, ReportContent} from './styles';
import SummaryModal from './SummaryModal';
import Wrapper from './Wrapper';
import YearSelector from './YearSelector';

export const SummaryColumn = styled(BlockColumn)(({theme}) => ({
  '& > div': {
    '&:nth-of-type(1) > h6': {
      color: theme.palette.info.main,
    },

    '&:nth-of-type(2) > h6': {
      color: theme.palette.warning.main,
    },

    '&:nth-of-type(3) > h6': {
      color: theme.palette.success.main,
    }
  },
}));

const Summary = (
  {
    title,
    period: {
      amount,
      cost,
      profit,
      count,
      recurring: {amount: recurringAmount, cost: recurringCost, profit: recurringProfit, count: recurringCount},
      fixed: {amount: fixedAmount, cost: fixedCost, profit: fixedProfit, count: fixedCount},
    },
    onSelect
  }
) => (
  <BlockContainer>
    <h3>{title}</h3>
    <BlockRow>
      <SummaryColumn className="interactive">
        <h4 onClick={() => onSelect(title, null)}>Total ({count} Payments)</h4>
        <div>
          <h5>Revenue:</h5>
          <h6><Currency amount={amount}/></h6>
        </div>
        <div>
          <h5>Cost:</h5>
          <h6><Currency amount={cost}/></h6>
        </div>
        <div>
          <h5>Profit:</h5>
          <h6><Currency amount={profit}/></h6>
        </div>
      </SummaryColumn>
    </BlockRow>
    <BlockRow>
      <SummaryColumn className="interactive">
        <h4 onClick={() => onSelect(title, 'recurring')}>Recurring ({recurringCount} Payments)</h4>
        <div>
          <h5>Revenue:</h5>
          <h6><Currency amount={recurringAmount}/></h6>
        </div>
        <div>
          <h5>Cost:</h5>
          <h6><Currency amount={recurringCost}/></h6>
        </div>
        <div>
          <h5>Profit:</h5>
          <h6><Currency amount={recurringProfit}/></h6>
        </div>
      </SummaryColumn>
      <SummaryColumn className="interactive">
        <h4 onClick={() => onSelect(title, 'fixed')}>Fixed ({fixedCount} Payments)</h4>
        <div>
          <h5>Revenue:</h5>
          <h6><Currency amount={fixedAmount}/></h6>
        </div>
        <div>
          <h5>Cost:</h5>
          <h6><Currency amount={fixedCost}/></h6>
        </div>
        <div>
          <h5>Profit:</h5>
          <h6><Currency amount={fixedProfit}/></h6>
        </div>
      </SummaryColumn>
    </BlockRow>
  </BlockContainer>
);

const SubscriptionPayments = () => {
  const services = useContext(ServiceContext);
  const [selectedYear, setSelectedYear] = useState(null);
  const years = useRef({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [canCheck, setCanCheck] = useState(false);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (selectedYear) {
      if (Object.keys(years.current).indexOf(`${selectedYear}`) < 0) {
        setLoading(`Loading ${selectedYear}...`);
        services.reporting.getPaymentYear(selectedYear).then((response) => {
          years.current[`${selectedYear}`] = response;
          setReport(response);
          setLoading('');
        }).catch(() => setLoading(''))
      } else {
        setReport(years.current[`${selectedYear}`]);
      }
    } else {
      setSelectedYear(parseInt(moment().format('YYYY')));
    }
  }, [services, selectedYear]);

  const handleExport = useCallback(() => {
    services.reporting.downloadExport().then(() => null).catch(() => null);
  }, [services]);

  const handleSelect = useCallback((period, type) => {
    const match = report.months.find(({title}) => title === period);
    let recurring = [];
    let fixed = [];
    if (match) {
      recurring = match.recurring.payments;
      fixed = match.fixed.payments;
    } else {
      for (let month of report.months) {
        recurring = [...recurring, ...month.recurring.payments];
        fixed = [...fixed, ...month.fixed.payments];
      }
    }
    let payments = [];
    if (!type || type === 'recurring') {
      payments = [...payments, ...recurring];
    }
    if (!type || type === 'fixed') {
      payments = [...payments, ...fixed];
    }
    payments.sort((a, b) => moment(a.date).isBefore(moment(b.date)) ? -1 : 1);
    setPayments(payments);
    setShowDetail(true);
    let title = 'All';
    if (type === 'recurring') {
      title = 'Recurring';
    } else if (type === 'fixed') {
      title = 'Fixed';
    }
    title = `${report.title} - ${title} Payments`;
    if (match) {
      title = `${match.title} ${title}`;
    }
    setDetailTitle(title);
    setCanCheck(!type && !!match);
  }, [report]);

  return (
    <Wrapper title="Subscription Payments" loading={loading} crumbs={[
      ...Wrapper.standardCrumbs,
      {title: 'Subscription Payments'},
    ]}>
      <Container className={loading === '' ? '' : 'loading'}>
        <Buttons>
          <Button onClick={handleExport} disabled={loading !== ''} variant="contained"><Download/> Export All</Button>
        </Buttons>
        <ReportContent>
          <YearSelector year={selectedYear} onChange={setSelectedYear} loading={loading}/>
          {report ? (
            <Blocks>
              <Summary title="Annual Totals" period={report} onSelect={handleSelect}/>
              <Break/>
              {report.months.map((month, index) => (
                <Summary title={month.title} period={month} onSelect={handleSelect} key={index}/>
              ))}
            </Blocks>
          ) : null}
          <SummaryModal
            open={showDetail}
            onClose={() => setShowDetail(false)}
            title={detailTitle}
            showCheck={canCheck}
            payments={payments}
          />
        </ReportContent>
      </Container>
    </Wrapper>
  );
}

export default SubscriptionPayments;
